import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// INTERFACES
import { PaginationConfig } from '../../../shared/configs';
import {
  IGetApiResponse,
  IGetApiResponseMeta,
  IOmsStore,
} from '../../../shared/interfaces';

// SERVICES
import { OmsStoreService } from '../../../shared/services/api/oms-store.service';

// CONFIGS

@Component({
  selector: 'lum-app-integration-account-oms-store-control',
  templateUrl: './app-integration-account-oms-store-control.component.html',
  styleUrls: ['./app-integration-account-oms-store-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppIntegrationAccountOmsStoreControlComponent),
      multi: true,
    },
  ],
})
export class AppIntegrationAccountOmsStoreControlComponent
  implements OnInit, OnDestroy, OnChanges
{
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  private readonly _syncLoading = new BehaviorSubject<boolean>(<boolean>false);
  syncLoading$ = this._syncLoading.asObservable();

  public omsStores: IOmsStore[] = [];
  public displayValue: string;

  private onChange: any = () => {};
  private onTouched: any = () => {};

  paginationConfig = PaginationConfig;
  queryParams: {
    [param: string]: string | string[] | boolean;
  } = {};
  meta: IGetApiResponseMeta = null;

  selectedOmsStore: Partial<IOmsStore>;
  private onDestroy$ = new Subject();

  @Input() appIntegrationAccountId: number;
  @Input() excludeUsedOmsStoreIds: boolean = false;

  constructor(
    private omsStoreService: OmsStoreService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['appIntegrationAccountId'] &&
      !changes['appIntegrationAccountId'].firstChange
    ) {
      this.displayValue = '';
      if (changes['appIntegrationAccountId'].currentValue !== null) {
        this.resetValues();
        this.getRecords();
      } else {
        this.resetValues();
      }
    }
  }

  writeValue(value: any): void {
    if (!value || value === null) {
      return;
    }
    this.getById(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // You can implement this if you need to handle disabling the component
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit(): void {
    this.initQueryParams();
    this.getRecords();
    this._loading.subscribe((loading: boolean) => {
      if (!loading) {
        this._syncLoading.next(false);
      }
    });
  }

  onSelectEvent(selectedOmsStore: IOmsStore) {
    if (this.selectedOmsStore && this.selectedOmsStore.id === selectedOmsStore.id) {
      return;
    }
    this.displayValue = selectedOmsStore.storeName;
    this.selectedOmsStore = selectedOmsStore;
    this.onChange(this.selectedOmsStore.id);
  }

  onChangeEvent(keyword: string) {
    this.resetValues();
    this.getRecords(keyword);
  }

  getById(id: number) {
    this.omsStoreService
      .getById(this.appIntegrationAccountId, id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (record: IOmsStore) => {
          this.onSelectEvent(record);
          this.changeDetectorRef.detectChanges();
        },
        error: (e) => {},
      });
  }

  private initQueryParams(): void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
    };
    if (this.excludeUsedOmsStoreIds) {
      this.queryParams = { ...this.queryParams, excludeUsedOmsStoreIds: true };
    }
  }

  resetValues() {
    this.onChange(null);
    this.omsStores = [];
  }

  getRecords(searchQuery?: string | null, synchronous: boolean = false) {
    if (!this.appIntegrationAccountId) {
      return;
    }
    const params: any = { ...this.queryParams };
    if (searchQuery) {
      params.searchQuery = searchQuery;
    }
    if (synchronous) {
      params.synchronous = true;
      this._syncLoading.next(true);
    }
    this._loading.next(true);
    this.omsStoreService
      .get(this.appIntegrationAccountId, params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response: IGetApiResponse) => {
          this.omsStores = [...this.omsStores, ...response.data];
          this.meta = response.meta;
          this._loading.next(false);
        },
        error: (e) => {
          this._loading.next(false);
        },
      });
  }

  onHardRefreshStores(): void {
    this.resetValues();
    this.getRecords(null, true);
  }

  onScrollPaginate(): void {
    if (this._loading.value || this.meta.current_page === this.meta.last_page) {
      return;
    }
    this.queryParams = {
      ...this.queryParams,
      page: (Number(this.meta.current_page) + 1).toString(),
    };
    this.getRecords();
  }
}
